import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { getJbpKeyInitiativeCrud } from 'legacy/lut/components/widgets/public/partner360/actionsPlan/keyInitiatives'
import { requestCommitmentStatuses } from 'legacy/lut/features/partner360/commitment'
import {
  requestPartnerData,
  selectFinancialYear,
  selectPartner360Data,
  selectPartnerAccess,
} from 'legacy/lut/features/partner360/configuration'
import { CommitmentReminderActionTypeEnum } from 'legacy/lut/services/partner360/commitment/models/ICommitmentReminder'
import { type ComponentType, type ReactNode, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { useAppDispatch } from 'cng-common/hooks/useAppDispatch'
import { getUrlParams, getUrlWithParams } from 'core-components/helpers/url'
import { TPopupVertical } from 'core-components/library/popup'
import { Tooltip } from 'core-components/library/tooltip'
import { selectRouteParams } from 'core-components/sitebuilder/store/selectors/selectRouteParams'
import { useNavigate } from 'core/hooks/useNavigate'
import { ReactComponent as LockIcon } from 'partner360/components/commitmentReminder/components/planReviewActions/components/actionsPopup/assets/LockIcon.svg'
import { PlanReviewKeyInitiative } from 'partner360/components/commitmentReminder/components/planReviewActions/components/planReviewKeyInitiative/PlanReviewKeyInitiative'
import {
  FINANCIAL_SECTION_URL_VALUE,
  P360_GLOBAL_PAGE_URL,
  P360_PAGE_URL,
  PLANNING_METRIC_URL_SEGMENT,
  PLANNING_SECTION_URL_SEGMENT,
} from 'partner360/configs'
import {
  normalizeFinancialMetricMode,
  parseFinancialMetricModes,
} from 'partner360/entities/financialMetricMode'
import { p360TabsIds } from 'partner360/entities/partnerCompleteness'
import { PartnerCommitmentTranslations } from 'partner360/entities/partnerCompleteness'
import { useGetPlanFinancialsQuery } from 'partner360/entities/planFinancials'
import { AcknowledgmentStatusDialog } from 'partner360/features/acknowledgmentStatusDialog'
import { KeyInitiativesReviewModal } from 'partner360/features/keyInitiativesReview'
import { PartnerDevicesInfoModal } from 'partner360/features/partnerDevicesInfo'
import { SobActualsModal } from 'partner360/features/sobActualsReview'
import type { TnetReviewModalProps } from 'partner360/features/tnetReviewModal/model/types'
import { TnetReviewModal as TnetReviewModalComponent } from 'partner360/features/tnetReviewModal/ui/TnetReviewModal'
import { scrollToElement } from 'partner360/helpers/scrollToElement'
import { partner360WidgetsBus } from 'partner360/lib/partner360WidgetsBus'
import {
  normalizePlanReviewActionParams,
  resolvePlanReviewActualsValueTypeActionParam,
  resolvePlanReviewBySubCategoryActionParam,
  resolvePlanReviewCategoryActionParam,
} from 'partner360/lib/planReviewActionParams'
import { NetworkDebugSource } from 'shared/lib/networkDebug'

import type { IFinancialCockpitPendingAction } from '../../../api/types'
import { getOfferingTitleKey } from '../../../lib/getOfferingTitleKey'
import { invalidateFinancialCockpitData } from '../../../model/financialCockpitSlice'
import { selectCockpitPartnerData, selectEffectiveCockpitFYear } from '../../../model/selectors'
import { useCockpitSectionNavigation } from '../../components/cockpitSectionNavigation'

import styles from './usePendingActionInvocation.module.scss'

const TnetReviewModal = TnetReviewModalComponent as ComponentType<TnetReviewModalProps>

export interface IUsePendingActionInvocationResult {
  /**
   * What a row's control calls: navigates the action away, or opens the dialog that finishes it.
   * It answers `true` when the action navigated, so a caller drawn over the page — an expanded
   * tile, a popup — can close itself and let the reader see where they were taken.
   */
  selectAction: (action: IFinancialCockpitPendingAction) => boolean
  /** The action a dialog currently stands on — `null` while no dialog is open. */
  selectedAction: IFinancialCockpitPendingAction | null
  /** Whether that action is still waiting on the financials its dialog opens against. */
  isActionBusy: (action: IFinancialCockpitPendingAction) => boolean
  /** The lock a blocked row wears, with the tooltip naming what has to be finished first. */
  renderBlockedTooltip: (blockedBy?: string, iconClassName?: string) => ReactNode
  /** Every dialog the actions can open, to be mounted once by whoever draws the rows. */
  modals: ReactNode
}

/**
 * Everything it takes to act on a pending action, with no opinion on how its row is drawn.
 *
 * An action is finished in one of two ways: it navigates to the section that holds it, or it opens
 * the dialog that submits it — six of them across the action types, each needing the partner, the
 * fiscal year, the offering and, for the SOB and T-Net reviews, the plan financials of the category
 * the action names. Once a dialog confirms, the statuses behind the cockpit are stale: the
 * commitment statuses are re-read, the key initiative queries dropped, the partner data re-requested
 * and the planning widgets told to refresh, each only for the action types they belong to.
 *
 * That weight is the reason this is a hook rather than a component. The pending-actions tile draws
 * its rows one way and the attention-status cards draw theirs another; both need the same dialogs,
 * the same refresh and the same blocked-by lock, and neither should own a copy of them.
 *
 * The offering is the scope: a dialog opened against the wrong plan would submit against it, so the
 * caller passes the offering its rows belong to and keeps mixed lists apart.
 */
export const usePendingActionInvocation = (
  offering: ProgramTypes
): IUsePendingActionInvocationResult => {
  const [selectedAction, setSelectedAction] = useState<IFinancialCockpitPendingAction | null>(null)
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>()
  const navigate = useNavigate()
  const t = useSafetyTranslation()
  const { isRemote, openSection } = useCockpitSectionNavigation()

  const pagePartner = useSelector(selectPartner360Data)
  const pageFYear = useSelector(selectFinancialYear)
  const { isDistributor } = useSelector(selectPartnerAccess)

  /* The manager cockpit stands on no partner's page: there the card is scoped to one of its
     partners, so the dialogs it can open act on that partner and the year the cockpit reads. */
  const cockpitPartner = useSelector(selectCockpitPartnerData)[0]
  const cockpitFYear = useSelector(selectEffectiveCockpitFYear)
  const isRemotePartner = isRemote && Boolean(cockpitPartner)

  const partner360Data = useMemo(
    () =>
      isRemotePartner && cockpitPartner
        ? {
            partnerId: cockpitPartner.PartnerId,
            partnerName: cockpitPartner.PartnerName,
            locationId: cockpitPartner.LocationId,
            country: cockpitPartner.Country,
            geoRegion: cockpitPartner.GeoRegion,
          }
        : cockpitPartner?.PartnerId === pagePartner.partnerId && cockpitPartner.PartnerName
          ? { ...pagePartner, partnerName: cockpitPartner.PartnerName }
          : pagePartner,
    [cockpitPartner, isRemotePartner, pagePartner]
  )
  const fyear = isRemotePartner ? cockpitFYear : pageFYear

  const dispatch = useAppDispatch()

  const routeParams = useSelector(selectRouteParams)

  const isSobActionSelected =
    selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JbpPatSob

  const shouldFetchFinData =
    isSobActionSelected ||
    selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JbpPatTnet

  const selectedActionParams = useMemo(
    () => normalizePlanReviewActionParams(selectedAction?.ActionParam),
    [selectedAction?.ActionParam]
  )
  const isSobBySubCategory =
    isSobActionSelected && resolvePlanReviewBySubCategoryActionParam(selectedActionParams)

  const {
    data = [],
    isLoading,
    isFetching,
  } = useGetPlanFinancialsQuery(
    {
      partnerId: partner360Data.partnerId,
      fYear: fyear,
      programType: offering,
      category: selectedCategory,
      isRollUp: false,
      isBySubCategory: isSobBySubCategory,
    },
    {
      skip: !partner360Data.partnerId || !fyear || !offering || !shouldFetchFinData,
    }
  )

  const isP360Page = useMemo(() => {
    return [P360_PAGE_URL, P360_GLOBAL_PAGE_URL].includes(routeParams.page)
  }, [routeParams.page])

  const selectedActionCategory = resolvePlanReviewCategoryActionParam(selectedActionParams)
  const selectedActionActualsValueType =
    resolvePlanReviewActualsValueTypeActionParam(selectedActionParams)

  const finData = useMemo(() => {
    return data.map((el) => ({ ...el, Category: el.Category || selectedActionCategory }))
  }, [data, selectedActionCategory])

  const metricMode = normalizeFinancialMetricMode(data[0]?.NetNdp) ?? 'net'
  const reviewMetricMode =
    normalizeFinancialMetricMode(selectedActionActualsValueType) ?? metricMode
  const availableMetricModes = useMemo(() => {
    const selectedActionMetricMode = normalizeFinancialMetricMode(selectedActionActualsValueType)

    if (selectedActionMetricMode) {
      return [selectedActionMetricMode]
    }

    return parseFinancialMetricModes(data[0]?.NetNdp)
  }, [data, selectedActionActualsValueType])

  const goTo = useCallback(
    (url: string) => {
      scrollToElement(p360TabsIds)
      const params = getUrlParams(url)
      const newUrl = getUrlWithParams(params)
      navigate(newUrl)
    },
    [navigate]
  )

  const resetData = useCallback(
    (action: IFinancialCockpitPendingAction | null, needFetchFinData?: boolean) => {
      if (action?.ActionType === CommitmentReminderActionTypeEnum.JBPPatInitiative) {
        getJbpKeyInitiativeCrud().removeQueries(true)
      }

      if (action?.ActionType === CommitmentReminderActionTypeEnum.JBPPatMif) {
        dispatch(
          requestPartnerData({
            partnerId: partner360Data.partnerId,
            fYear: fyear,
            force: true,
          })
        )
      }

      if (
        action?.ActionType &&
        [
          CommitmentReminderActionTypeEnum.JbpPatSob,
          CommitmentReminderActionTypeEnum.JbpPatTnet,
        ].includes(action.ActionType)
      ) {
        partner360WidgetsBus.notify('planningRefresh')
      }

      if (needFetchFinData) {
        const actionParams = normalizePlanReviewActionParams(action?.ActionParam)

        setSelectedCategory(resolvePlanReviewCategoryActionParam(actionParams))
      }
    },
    [dispatch, fyear, partner360Data.partnerId]
  )

  const onClose = useCallback(() => {
    setSelectedAction(null)
    setSelectedCategory(undefined)
  }, [])

  const closeAcknowledgeAction = useCallback(() => {
    setSelectedAction(null)
  }, [])

  /* Signing moves the plan's status on the backend, so the cockpit's payload is refetched. */
  const onAcknowledgmentUpdated = useCallback(() => {
    dispatch(invalidateFinancialCockpitData())
  }, [dispatch])

  const goByActionLink = useMemo(() => {
    if (!selectedAction?.ActionLink) {
      return undefined
    }

    return () => {
      if (selectedAction?.ActionLink) {
        goTo(selectedAction.ActionLink)
        onClose()
      }
    }
  }, [goTo, onClose, selectedAction])

  const goToPlanning = useCallback(() => {
    const categoryPath = selectedActionCategory
      ? `/${PLANNING_SECTION_URL_SEGMENT}/${selectedActionCategory}`
      : ''

    goTo(
      `jbp-tab/${FINANCIAL_SECTION_URL_VALUE}${categoryPath}/${PLANNING_METRIC_URL_SEGMENT}/${reviewMetricMode}`
    )
    onClose()
  }, [goTo, onClose, reviewMetricMode, selectedActionCategory])

  const onActionExecute = useCallback(
    (action: IFinancialCockpitPendingAction | null, needFetchFinData?: boolean) => async () => {
      dispatch(
        requestCommitmentStatuses({
          partnerId: partner360Data.partnerId,
          fYear: fyear,
        })
      )

      resetData(action, needFetchFinData)

      /* Away from the partner's page nothing else re-reads the partner, so the cockpit's own
         payload is refetched for the status the dialog answered. */
      if (isRemotePartner) {
        dispatch(invalidateFinancialCockpitData())
      }
    },
    [dispatch, fyear, isRemotePartner, partner360Data.partnerId, resetData]
  )

  const selectAction = useCallback(
    (action: IFinancialCockpitPendingAction) => {
      if (action?.ActionBlockedBy) {
        return false
      }

      /* The manager cockpit stands on no partner's page, so most actions are finished on the
         partner's page in a tab of its own: the section its link names, or the page itself. The SOB
         dialog reads everything it needs by the partner's id, so it opens right here, as it does on
         the partner's own cockpit. */
      const opensHere =
        isRemotePartner && action.ActionType === CommitmentReminderActionTypeEnum.JbpPatSob

      if (isRemote && !opensHere) {
        openSection(action?.ActionLink ? getUrlParams(action.ActionLink) : {})

        return false
      }

      if (
        action?.ActionLink &&
        ![
          CommitmentReminderActionTypeEnum.JBPPatKi,
          CommitmentReminderActionTypeEnum.JBPPatAcknowledgment,
        ].includes(action.ActionType)
      ) {
        goTo(action.ActionLink)

        return true
      } else if (
        action.ActionType === CommitmentReminderActionTypeEnum.JBPPatAcknowledgment &&
        isP360Page &&
        action?.ActionLink
      ) {
        goTo(action.ActionLink)

        return true
      } else {
        const actionParams = normalizePlanReviewActionParams(action.ActionParam)
        const actionCategory = resolvePlanReviewCategoryActionParam(actionParams)

        if (
          [
            CommitmentReminderActionTypeEnum.JbpPatSob,
            CommitmentReminderActionTypeEnum.JbpPatTnet,
          ].includes(action.ActionType)
        ) {
          setSelectedCategory(actionCategory)
        }
        setSelectedAction(action)
      }

      return false
    },
    [goTo, isP360Page, isRemote, isRemotePartner, openSection]
  )

  /* Only the row whose dialog is being opened waits: the financials are fetched for the selected
     action alone, so every other row keeps its control ready rather than going busy with it. */
  const isActionBusy = useCallback(
    (action: IFinancialCockpitPendingAction) =>
      (isLoading || isFetching) && selectedAction === action,
    [isFetching, isLoading, selectedAction]
  )

  /* The lock is handed out whole — icon and tooltip together — because a row that only got the
     tooltip's text would have to carry its own copy of the icon and of the popup around it. The
     class stays the caller's: the tile's lock and a card's are sized by their own row. */
  const renderBlockedTooltip = useCallback(
    (blockedBy?: string, iconClassName?: string) => {
      if (!blockedBy) {
        return null
      }

      const blockedActions = blockedBy
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
        .map((item) => t(item))
        .join(', ')

      return (
        <Tooltip
          verticalDirection={TPopupVertical.Bottom}
          tipContent={() => (
            <div className={styles.tooltipContent}>
              <Translation
                span
                text={PartnerCommitmentTranslations.BlockedActions}
                replaceParams={[{ key: '{actions}', value: blockedActions }]}
              />
            </div>
          )}
        >
          <LockIcon className={iconClassName} />
        </Tooltip>
      )
    },
    [t]
  )

  /* All six dialogs are mounted together and each shows itself off the selected action's type: the
     caller mounts the node once, beside its rows, and never has to know which row opened what. */
  const modals = (
    <>
      <TnetReviewModal
        show={selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JbpPatTnet}
        partner={{
          region: partner360Data.geoRegion ?? '',
          country: partner360Data.country,
          partnerName: partner360Data.partnerName,
          pid: partner360Data.partnerId,
          locationId: partner360Data.locationId,
        }}
        category={selectedActionCategory || null}
        finRows={data}
        metricMode={metricMode}
        reviewMetricMode={reviewMetricMode}
        currentFiscalYear={fyear}
        programType={offering}
        onClose={onClose}
        onGoToPlanning={goToPlanning}
        onConfirmed={onActionExecute(selectedAction, true)}
      />
      <SobActualsModal
        show={isSobActionSelected}
        pid={partner360Data.partnerId}
        offering={offering}
        region={partner360Data.geoRegion ?? ''}
        country={partner360Data.country ?? ''}
        partnerName={partner360Data.partnerName ?? ''}
        locationId={partner360Data.locationId ?? ''}
        category={selectedActionCategory || null}
        isBySubCategory={isSobBySubCategory}
        defaultMetricMode={reviewMetricMode}
        availableMetricModes={availableMetricModes}
        currentFiscalYear={fyear}
        finData={finData}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, true)}
      />
      <PartnerDevicesInfoModal
        show={selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JBPPatMif}
        partnerId={partner360Data.partnerId}
        offering={offering}
        fYear={fyear}
        isEditable={true}
        partnerSummary={{
          region: partner360Data.geoRegion ?? '',
          country: partner360Data.country,
          partnerName: partner360Data.partnerName,
          pid: partner360Data.partnerId,
          locationId: partner360Data.locationId,
        }}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, false)}
      />

      <PlanReviewKeyInitiative
        selectedAction={selectedAction}
        partnerId={partner360Data.partnerId}
        programType={offering}
        fyear={fyear}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, false)}
      />

      <KeyInitiativesReviewModal
        show={selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JBPPatKi}
        defaultMetricMode={reviewMetricMode}
        availableMetricModes={availableMetricModes}
        partnerId={partner360Data.partnerId}
        isDistributor={isDistributor}
        offering={offering}
        region={partner360Data.geoRegion ?? ''}
        country={partner360Data.country ?? ''}
        partnerName={partner360Data.partnerName ?? ''}
        locationId={partner360Data.locationId ?? ''}
        currentFiscalYear={fyear}
        onClose={onClose}
        onConfirmed={onActionExecute(selectedAction, false)}
        onGoToPlanning={goByActionLink}
      />

      <NetworkDebugSource name="Pending actions">
        <AcknowledgmentStatusDialog
          show={
            selectedAction?.ActionType === CommitmentReminderActionTypeEnum.JBPPatAcknowledgment
          }
          onClose={closeAcknowledgeAction}
          onUpdated={onAcknowledgmentUpdated}
          partnerId={partner360Data.partnerId}
          fYear={fyear}
          offering={offering}
          offeringLabel={t(getOfferingTitleKey(offering))}
          partnerSummary={{
            region: partner360Data.geoRegion ?? '',
            country: partner360Data.country,
            partnerName: partner360Data.partnerName,
            pid: partner360Data.partnerId,
            locationId: partner360Data.locationId,
          }}
        />
      </NetworkDebugSource>
    </>
  )

  return { selectAction, selectedAction, isActionBusy, renderBlockedTooltip, modals }
}
